import { useBlocker } from "@tanstack/react-router";
import type { TikTokRegion } from "@unbox/api";
import type { MerchantPreferredLanguage } from "@unbox/db";
import { AlertTriangle, File02, InfoCircle, Save01 } from "@untitledui/icons";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";
import {
  type MerchantCreatorAccount,
  useAddMerchantCreators,
  useDeleteMerchantDetailsLogo,
  useMerchantDetailsSettings,
  useRemoveMerchantCreator,
  useUpdateMerchantName,
  useUpdateMerchantPreferredLanguage,
  useUploadMerchantDetailsLogo,
} from "@/api/merchant-details-settings";
import { useMerchantDomainKnowledge, useSaveSafetyConfig } from "@/api/merchant-risks";
import { LogoUploader } from "@/components/application/logo-uploader/logo-uploader";
import { Button } from "@/components/base/buttons/button";
import { Select } from "@/components/base/select/select";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { PageLayout } from "@/components/page-layout";
import { UnsavedChangesDialog } from "@/components/unsaved-changes-dialog";
import { cx } from "@/utils/cx";
import { normalizeCreatorId } from "@/utils/formatters";
import { parseKeywordList, serializeKeywordList } from "@/utils/keyword-lists";
import { SUPPORTED_RISK_EXPLANATION_LANGUAGES } from "@/utils/risk-explanation-languages";

const LANGUAGE_SETTING_REGIONS: TikTokRegion[] = ["US", "UK", "DE", "FR", "ES", "TH", "VN", "ID", "MY", "SG", "PH"];

/** Domain-knowledge key holding the merchant's owned/associated brand names. Shared
 *  across the app: the safety pipeline substitutes it into the third-party risks. */
const OWN_BRANDS_KEY = "own_brands";

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((value, i) => value === b[i]);

/** Appends `value` unless it is blank or already listed under a different casing. */
const withBrand = (brands: string[], value: string): string[] => {
  const trimmed = value.trim();
  if (!trimmed || brands.some((brand) => brand.toLowerCase() === trimmed.toLowerCase())) return brands;
  return [...brands, trimmed];
};

/** Shown on the fields the customer can never edit. The handle is captured from the
 *  shop when we onboard the merchant, while the region is read off the shop itself. */
const READ_ONLY_HANDLE_TOOLTIP =
  "This value is read-only. It's set from your TikTok Shop account during onboarding and can't be edited in Unbox.";
const READ_ONLY_REGION_TOOLTIP = "This value is read-only. It comes from TikTok Shop and can't be edited in Unbox.";

/** Two-column field row: label + description on the left, control on the right. */
const FieldRow = ({
  label,
  description,
  required,
  tooltip,
  children,
  className,
}: {
  label: string;
  description?: string;
  required?: boolean;
  tooltip?: string;
  children: ReactNode;
  className?: string;
}) => (
  <div className={cx("flex flex-col gap-3 py-6 md:flex-row md:gap-8", className)}>
    <div className="md:w-64 shrink-0">
      <div className="flex items-center gap-1.5">
        <p className="text-sm font-semibold text-secondary">
          {label} {required && <span className="text-brand-tertiary">*</span>}
        </p>
        {tooltip && (
          <Tooltip title={tooltip}>
            <TooltipTrigger
              aria-label={`${label} info`}
              className="cursor-pointer text-fg-disabled hover:text-fg-quaternary_hover"
            >
              <InfoCircle className="size-3.5" />
            </TooltipTrigger>
          </Tooltip>
        )}
      </div>
      {description && <p className="mt-1 text-sm text-quaternary">{description}</p>}
    </div>
    <div className="flex-1 max-w-xl">{children}</div>
  </div>
);

/** A single removable chip. `showAvatar` renders a leading logo/initials box (used
 *  for creator accounts); brand-name chips are plain text pills. */
const Chip = ({
  label,
  imageUrl,
  showAvatar = true,
  onRemove,
}: {
  label: string;
  imageUrl?: string | null;
  showAvatar?: boolean;
  onRemove: () => void;
}) => (
  <span className="inline-flex items-center gap-1.5 rounded-md border border-secondary bg-primary py-0.5 pl-1.5 pr-1.5 text-sm text-secondary">
    {showAvatar &&
      (imageUrl ? (
        <img src={imageUrl} alt="" className="size-4 rounded-sm object-cover" />
      ) : (
        <span className="flex size-4 items-center justify-center rounded-sm bg-secondary text-[10px] font-medium text-tertiary">
          {label.charAt(0).toUpperCase()}
        </span>
      ))}
    <span className="font-medium">{label}</span>
    <button
      type="button"
      className="text-quaternary transition-colors hover:text-primary"
      onClick={onRemove}
      aria-label={`Remove ${label}`}
    >
      ×
    </button>
  </span>
);

/** Chip list with an inline "add" text input (submit on Enter). The pending text is
 *  owned by the caller, which needs to read it before it becomes a chip. */
const ChipInput = ({
  chips,
  placeholder,
  disabled,
  hint,
  value,
  onValueChange,
  onSubmit,
}: {
  chips: ReactNode;
  placeholder: string;
  disabled?: boolean;
  hint?: string;
  value: string;
  onValueChange: (value: string) => void;
  onSubmit: (value: string) => void;
}) => {
  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
    onValueChange("");
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-primary bg-primary px-2.5 py-2 ring-inset focus-within:ring-2 focus-within:ring-brand">
        {chips}
        <input
          type="text"
          placeholder={placeholder}
          disabled={disabled}
          className="min-w-[120px] flex-1 bg-transparent text-sm text-primary outline-none placeholder:text-placeholder disabled:cursor-not-allowed"
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          onBlur={submit}
        />
      </div>
      {hint && <p className="mt-2 text-sm font-normal text-tertiary">{hint}</p>}
    </div>
  );
};

const ReadOnlyField = ({ value, prefix }: { value: string | null; prefix?: string }) => (
  <div className="flex items-stretch overflow-hidden rounded-lg border border-primary bg-disabled_subtle text-sm text-tertiary">
    {prefix && <span className="flex items-center border-r border-primary px-3 py-2 text-quaternary">{prefix}</span>}
    <span className="flex items-center px-3 py-2">{value || "—"}</span>
  </div>
);

export function MerchantDetailsPage() {
  const { data, isLoading } = useMerchantDetailsSettings();

  const { data: domainKnowledge } = useMerchantDomainKnowledge();

  const updateName = useUpdateMerchantName();
  const updatePreferredLanguage = useUpdateMerchantPreferredLanguage();
  const uploadLogo = useUploadMerchantDetailsLogo();
  const deleteLogo = useDeleteMerchantDetailsLogo();
  const addCreators = useAddMerchantCreators();
  const removeCreator = useRemoveMerchantCreator();
  const saveConfig = useSaveSafetyConfig();

  // Name and associated brands are edited locally and persisted together by "Save
  // Changes". Logo and marketing accounts apply immediately through their own actions.
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [brandsDraft, setBrandsDraft] = useState<string[] | null>(null);
  const [brandInput, setBrandInput] = useState("");
  const [creatorInput, setCreatorInput] = useState("");
  // Once set on the server this can never be un-set from here (only support can change it),
  // so the draft only ever matters before the first save.
  const [preferredLanguageDraft, setPreferredLanguageDraft] = useState<MerchantPreferredLanguage | null>(null);

  const name = nameDraft ?? data?.name ?? "";
  const trimmedName = name.trim();
  const hasNameChange = !!data && trimmedName.length > 0 && trimmedName !== data.name;

  // Associated brands live in domain knowledge (`own_brands`), the single list the
  // safety pipeline reads.
  const savedBrands = parseKeywordList(domainKnowledge?.[OWN_BRANDS_KEY]);
  const brands = brandsDraft ?? savedBrands;

  // Text still sitting in the input is appended on save, so it has to count as a
  // change before it becomes a chip.
  const brandsToSave = withBrand(brands, brandInput);
  const hasBrandChange = !sameList(brandsToSave, savedBrands);

  const isLanguageLocked = !!data?.preferredLanguage;
  const hasLanguageChange = !isLanguageLocked && preferredLanguageDraft !== null;

  // The merchant name is required, so nothing saves while it is blank.
  const hasChanges = trimmedName.length > 0 && (hasNameChange || hasBrandChange || hasLanguageChange);
  const isSaving = updateName.isPending || saveConfig.isPending || updatePreferredLanguage.isPending;

  // Warn before navigating away (or closing the tab) with unsaved edits.
  const blocker = useBlocker({
    shouldBlockFn: () => hasChanges,
    enableBeforeUnload: () => hasChanges,
    withResolver: true,
  });

  // Brands go through the safety-config endpoint, the only merchant-admin-gated writer
  // of domain knowledge. The empty risk arrays leave the rest of the config untouched.
  // Returns whether the save succeeded, so the unsaved-changes dialog knows whether to
  // proceed with leaving.
  const handleSave = async (): Promise<boolean> => {
    if (!hasChanges || isSaving) return true;

    try {
      if (hasNameChange) await updateName.mutateAsync(trimmedName);
      if (hasLanguageChange && preferredLanguageDraft) {
        await updatePreferredLanguage.mutateAsync(preferredLanguageDraft);
      }
      if (hasBrandChange) {
        await saveConfig.mutateAsync({
          enabled: [],
          updated: [],
          disabled: [],
          domainKnowledge: { [OWN_BRANDS_KEY]: serializeKeywordList(brandsToSave) },
        });
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save changes");
      return false;
    }

    // The invalidated queries are still refetching, so the drafts have to hold what was
    // just saved rather than fall back to the values still in the cache.
    setNameDraft(trimmedName);
    setBrandsDraft(brandsToSave);
    setBrandInput("");
    toast.success("Merchant details saved");
    return true;
  };

  const handleUploadLogo = async (file: File) => {
    await uploadLogo.mutateAsync(file).catch((e) => {
      toast.error(e instanceof Error ? e.message : "Failed to upload logo");
      throw e;
    });
  };

  const handleDeleteLogo = async () => {
    await deleteLogo.mutateAsync().catch((e) => {
      toast.error(e instanceof Error ? e.message : "Failed to remove logo");
      throw e;
    });
  };

  const handleAddCreator = (rawCreatorId: string) => {
    const creatorId = normalizeCreatorId(rawCreatorId);
    if (!creatorId) return;
    addCreators.mutate([creatorId], {
      onSuccess: (res) => {
        if (res.failedToInsert.includes(creatorId)) {
          toast.error(`No creator found for ID "${rawCreatorId}"`);
        }
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to add creator"),
    });
  };

  const handleAddBrand = (value: string) => setBrandsDraft(withBrand(brands, value));

  const handleRemoveBrand = (value: string) => setBrandsDraft(brands.filter((brand) => brand !== value));

  const creators: MerchantCreatorAccount[] = data?.creators ?? [];

  return (
    <PageLayout
      title="Merchant details"
      icon={File02}
      subtitle="Manage how your brand appears in Unbox and associated creator accounts."
      callToAction={
        <Button
          color="primary"
          size="md"
          iconLeading={Save01}
          isDisabled={!hasChanges || isSaving}
          isLoading={isSaving}
          onClick={handleSave}
        >
          Save Changes
        </Button>
      }
    >
      {/* Card — fields are capped and centred so they don't stretch on wide screens. */}
      <div className="rounded-xl border border-secondary bg-primary px-8">
        <div className="mx-auto max-w-[950px] py-5">
          {isLoading || !data ? (
            <div className="py-16 text-center text-sm text-quaternary">Loading…</div>
          ) : (
            <div className="divide-y divide-border-secondary">
              <FieldRow
                label="Merchant Name"
                description="This is how it will look in the navigation so make sure caps and spelling are as desired"
                required
              >
                <input
                  type="text"
                  aria-label="Merchant Name"
                  className="w-full rounded-lg border border-primary bg-primary px-3 py-2 text-sm text-primary outline-none ring-inset focus:ring-2 focus:ring-brand"
                  value={name}
                  onChange={(e) => setNameDraft(e.target.value)}
                />
              </FieldRow>

              <FieldRow label="Merchant logo" description="Shown in the navigation for all users." required>
                <LogoUploader
                  currentLogoUrl={data.logoUrl}
                  onUpload={handleUploadLogo}
                  onDelete={handleDeleteLogo}
                  isUploading={uploadLogo.isPending}
                  isDeleting={deleteLogo.isPending}
                />
              </FieldRow>

              <FieldRow
                label="TikTok Shop handle"
                description="The TikTok Shop account we track for you."
                tooltip={READ_ONLY_HANDLE_TOOLTIP}
              >
                <ReadOnlyField value={data.shopHandle} prefix="@" />
              </FieldRow>

              <FieldRow
                label="Shop region"
                description="Location the TikTok shop is located in"
                tooltip={READ_ONLY_REGION_TOOLTIP}
              >
                <ReadOnlyField value={data.region} />
              </FieldRow>

              {(LANGUAGE_SETTING_REGIONS as readonly string[]).includes(data.region ?? "") && (
                <FieldRow
                  label="Risk explanation language"
                  description="Standard risk explanations sent to creators are translated into this language. Anything you write yourself is sent exactly as typed."
                  tooltip={
                    isLanguageLocked
                      ? "To change the language, email us to support@unbox-commerce.com."
                      : "You set this once. After saving, only support can change it, and it only applies to videos published from then on."
                  }
                >
                  {isLanguageLocked ? (
                    <Tooltip title="To change the language, email us to support@unbox-commerce.com.">
                      <TooltipTrigger aria-label="Risk explanation language locked" className="block w-full">
                        <ReadOnlyField
                          value={
                            SUPPORTED_RISK_EXPLANATION_LANGUAGES.find((l) => l.code === data.preferredLanguage)?.name ??
                            data.preferredLanguage
                          }
                        />
                      </TooltipTrigger>
                    </Tooltip>
                  ) : (
                    <>
                      <Select
                        aria-label="Risk explanation language"
                        placeholder="Choose language"
                        size="md"
                        items={SUPPORTED_RISK_EXPLANATION_LANGUAGES.map((l) => ({
                          id: l.code,
                          label: l.english ? `${l.name} · ${l.english}` : l.name,
                        }))}
                        selectedKey={preferredLanguageDraft}
                        onSelectionChange={(key) =>
                          setPreferredLanguageDraft(key ? (String(key) as MerchantPreferredLanguage) : null)
                        }
                      >
                        {(language) => (
                          <Select.Item key={language.id} id={language.id}>
                            {language.label}
                          </Select.Item>
                        )}
                      </Select>
                      {preferredLanguageDraft !== null && (
                        <div className="mt-3 flex flex-col gap-2.5 rounded-xl border border-warning-200 bg-warning-50 p-4">
                          <p className="flex items-center gap-2.5 text-sm font-semibold text-warning-700">
                            <AlertTriangle className="w-[18px] h-[18px] shrink-0" aria-hidden="true" />
                            This cannot be changed
                          </p>
                          <p className="text-[13px] leading-relaxed text-gray-800">
                            Applies to videos published after you save. Anything published before stays in English. Any
                            questions contact{" "}
                            <a
                              href="mailto:support@unbox-commerce.com"
                              className="font-semibold text-purple-950 underline decoration-dotted decoration-1 underline-offset-[3px] hover:decoration-solid"
                            >
                              support@unbox-commerce.com
                            </a>
                            .
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </FieldRow>
              )}

              <FieldRow
                label="Marketing accounts"
                description="TikTok accounts owned by your brand. Adding these lets us exclude them from reports and enable filtering."
              >
                <ChipInput
                  placeholder="Add Creator ID"
                  disabled={addCreators.isPending}
                  value={creatorInput}
                  onValueChange={setCreatorInput}
                  chips={creators.map((c) => (
                    <Chip
                      key={c.creatorId}
                      label={c.name}
                      imageUrl={c.thumbnail}
                      onRemove={() => removeCreator.mutate(c.creatorId)}
                    />
                  ))}
                  onSubmit={handleAddCreator}
                />
              </FieldRow>

              <FieldRow
                label="Associated brands"
                description="Other brands in your portfolio or family that you're happy to be mentioned alongside."
              >
                <ChipInput
                  placeholder="Add brand names"
                  disabled={isSaving}
                  value={brandInput}
                  onValueChange={setBrandInput}
                  hint="Only add companies you're genuinely linked to  this affects how their mentions are risk-assessed."
                  chips={brands.map((b) => (
                    <Chip key={b} label={b} showAvatar={false} onRemove={() => handleRemoveBrand(b)} />
                  ))}
                  onSubmit={handleAddBrand}
                />
              </FieldRow>
            </div>
          )}
        </div>
      </div>

      <UnsavedChangesDialog blocker={blocker} onSave={handleSave} isSaving={isSaving} />
    </PageLayout>
  );
}
